import type { IsNever } from '@direct-frontend/stdlib';

/**
 * Вспомогательный тип для реализации функций с опциональным аргументом, который зависят от дженерик-параметра
 *
 * @typeParam T - Тип аргумента. Если `never`, то на входе функция не будет ожидать этот аргумент
 *
 * @example
 * function someFunction<Arg = never>(...args: OptionalSpread<Arg>) {
 *     // ...
 * }
 *
 * someFunction();
 * someFunction<number>(1);
 * someFunction<string>('hello, world!');
 */
type OptionalSpread<T> = IsNever<T, [], [T]>;

/**
 * Обработчик собтия кастомного модульного события
 *
 * @typeParam Payload - Тип данных, которые могут быть переданы вместе с событием
 */
type EventHandler<Payload> = (...args: OptionalSpread<Payload>) => void;

export interface PubSubEvent<Payload = never> {
    publish(...args: OptionalSpread<Payload>): void;
    subscribe(eventHandler: EventHandler<Payload>): () => void;
}

/**
 * Создает кастомное модульное событие с возможностью подписки на него
 *
 * @typeParams Payload - Тип данных, которые могут быть переданы вместе с событием
 * @returns Событие с методами `.subscribe()` и `.publish()` для подписки на событие и отправки нового события
 *
 * @example
 * // Создание события
 * const someEvent = createEvent<{ someField: string }>();
 *
 * // Подписка на событие
 * someEvent.subscribe(payload => {
 *     console.log(payload.someField);
 * });
 *
 * // Отправка события
 * someEvent.publish({ someField: 'someValue' });
 */
export function createEvent<Payload = never>(): PubSubEvent<Payload> {
    let eventHandlers: EventHandler<Payload>[] = [];

    return {
        publish(...args: OptionalSpread<Payload>) {
            for (const eventHandler of eventHandlers) {
                eventHandler(...args);
            }
        },
        subscribe(eventHandler: EventHandler<Payload>) {
            eventHandlers.push(eventHandler);

            return function unsubscribe() {
                eventHandlers = eventHandlers.filter(storedEventHandler => storedEventHandler !== eventHandler);
            };
        },
    };
}
